import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { User } from 'viewbid-shared-lib';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/viewbid/state/app-state';
import { NotificationService } from 'src/app/viewbid/shared/services/notification.service';
import { Subject } from 'rxjs';
import {
    UsersService,
    GetDashboardData200Response,
    AuctionCatalogsService,
    ListCards200Response,
    BiddingService,
    GetMyBidHistory200Response,
    UserBidHistoryResponse,
} from '@viewbid/ts-angular-client';
import { money, VbBidTableRow, VbSnackbarService } from '@viewbid/ui';
import { ProfileService } from 'src/app/viewbid/shared/services/profile.service';
import { TranslateService } from '@ngx-translate/core';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import { ToggleService } from '../../../auctions/services/toggle.service';
import { userBidHistoryToBidTableRow } from 'src/app/viewbid/shared/utils/bid-history-ui-mapper.util';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';

/** Lot deep-link for a recent-bid row, keyed by the row id the shared mapper
 *  assigns (VbBidTableRow carries no auction/lot ids of its own). */
interface RecentBidTarget {
    auctionId: string;
    lotId: string;
}

@Component({
    selector: 'vb-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnDestroy, OnInit {
    private destroy$ = new Subject<void>();

    // Public properties for template
    public user: User | null = null;
    public notificationsCount: number = 0;
    public watchListCount: number = 0;
    public invoiceCount: number = 0;
    public registrationStatus!: string;
    public isLoading: boolean = true;
    public spendingCapMessage: string | null = null;
    public showBidLimit: boolean = false;
    public isMobile: boolean = false;
    private hasCard: boolean = false;

    /** Recent-bidding rows for vb-bid-table's 'recent' variant — the same
     *  shared mapping the My-bidding ledger uses, so status semantics agree. */
    public recentBidRows: VbBidTableRow[] = [];
    public recentBidsLoading: boolean = true;
    private recentBidsRaw: UserBidHistoryResponse[] = [];
    private recentBidTargets: Map<string, RecentBidTarget> = new Map();

    /** Compact digest — a taste of the ledger; "View all" is the full page. */
    private static readonly RECENT_BIDS_LIMIT: number = 5;

    constructor(
        private store: Store<AppState>,
        public notificationService: NotificationService,
        private profileService: ProfileService,
        private userService: UsersService,
        private auctionCatalogsService: AuctionCatalogsService,
        private biddingService: BiddingService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private deviceCheckService: DeviceCheckService,
        private toggleService: ToggleService,
        private router: Router,
        private serverClock: ServerClockService,
    ) {
        this.initializeSubscriptions();
    }

    /**
     * The Notifications stat card shows the unread count, so it opens the
     * header bell's dropdown (the actual notifications) rather than the
     * preferences panel. Same ToggleService path the bell itself uses —
     * onToggleClicked closes any other open header toggle first.
     */
    openNotificationsDropdown(): void {
        this.toggleService.onToggleClicked('isNotificationDropdownOpen', true);
    }
    ngOnInit(): void {
        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });
    }

    private initializeSubscriptions(): void {
        this.isLoading = true;
        this.fetchDashboardDetails();
        this.fetchCardStatus();
        this.fetchRecentBids();
        this.initializeUserSubscription();
    }

    private fetchRecentBids(): void {
        this.biddingService
            .getMyRecentBids(UserProfileComponent.RECENT_BIDS_LIMIT)
            .subscribe({
                next: (response: GetMyBidHistory200Response) => {
                    this.recentBidsRaw = response.data ?? [];
                    this.recentBidsLoading = false;
                    this.rebuildRecentBids();
                },
                error: () => {
                    // Secondary card — fall back to the quiet empty state.
                    this.recentBidsLoading = false;
                },
            });
    }

    /** Row status compares against the user id (winning/losing = high-bidder
     *  check) and the user can land after the bids — re-map on both arrivals.
     *  Fresh array reference each time so the OnPush table re-renders. */
    private rebuildRecentBids(): void {
        const currentUserId: string = this.user?.accountMappingsId ?? '';
        const nowMs: number = this.serverClock.now();
        this.recentBidRows = this.recentBidsRaw.map(
            (item: UserBidHistoryResponse): VbBidTableRow =>
                userBidHistoryToBidTableRow(item, currentUserId, nowMs),
        );
        this.recentBidTargets = new Map(
            this.recentBidRows.map(
                (row: VbBidTableRow, i: number): [string, RecentBidTarget] => [
                    row.id,
                    {
                        auctionId: this.recentBidsRaw[i].auctionId ?? '',
                        lotId: this.recentBidsRaw[i].lotId ?? '',
                    },
                ],
            ),
        );
    }

    /** Row body click → the lot page. Closed lots are only viewable by their
     *  winner — the table already renders non-navigable rows inert (same
     *  rule as the My-bidding ledger); this guard is defence in depth. */
    onRecentBidClick(row: VbBidTableRow): void {
        if (row.navigable === false) return;
        const target: RecentBidTarget | undefined = this.recentBidTargets.get(
            row.id,
        );
        if (!target?.auctionId || !target.lotId) return;
        this.router.navigate([
            '/auctions',
            target.auctionId,
            'lot',
            target.lotId,
        ]);
    }

    private fetchCardStatus(): void {
        this.auctionCatalogsService.listCards().subscribe({
            next: (response: ListCards200Response) => {
                this.hasCard = (response.data?.length ?? 0) > 0;
                if (this.user) {
                    this.updateUserRelatedStates(this.user);
                }
            },
            error: () => {
                this.hasCard = false;
            },
        });
    }

    private initializeUserSubscription(): void {
        this.store
            .select(selectUser)
            .pipe(
                takeUntil(this.destroy$),
                filter((user: User | null): user is User => user !== null),
                map(this.adjustUserSpendingCap),
            )
            .subscribe((user: User) => {
                this.updateUserRelatedStates(user);
            });
    }

    private fetchDashboardDetails(): void {
        this.userService.getDashboardData().subscribe({
            next: ({ data }: GetDashboardData200Response) => {
                this.profileService.updateUserStore();
                this.isLoading = false;
                this.invoiceCount = data.numInvoices ?? 0;
                this.notificationsCount = data.user?.numNotifications ?? 0;
                this.watchListCount = data.watchlist ?? 0;
            },
            error: () => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant('Users.getUserProfileError'),
                );
            },
        });
    }

    private updateUserRelatedStates(user: User): void {
        this.user = user;
        this.showBidLimit = this.shouldShowBidLimit(user);
        this.spendingCapMessage = this.getSpendingCapMessage(user);
        this.registrationStatus = user.registrationStatus ?? '';
        this.rebuildRecentBids();
    }

    /** Initials for the avatar disc — same derivation as the app header's
     *  avatar (see app-header-shell.toHeaderUser) so the two always agree. */
    get avatarInitials(): string {
        const firstname: string = this.user?.firstname ?? '';
        const lastname: string = this.user?.lastname ?? '';
        const email: string = this.user?.email ?? '';

        let initials: string;
        if (firstname && lastname) {
            initials = firstname[0] + lastname[0];
        } else if (firstname) {
            initials = firstname.substring(0, 2);
        } else {
            initials = email.substring(0, 2);
        }

        return initials.toUpperCase();
    }

    /** Verified chip source: the User model carries no boolean verified
     *  flag — the app's only status semantic is registrationStatus, where
     *  'PENDING' means "not yet verified by support" (see
     *  getSpendingCapMessage). Any other non-empty status is treated as an
     *  approved/verified account. */
    get isVerified(): boolean {
        return !!this.registrationStatus && !this.isPendingVerification;
    }

    /** True while support has not yet approved the account. */
    get isPendingVerification(): boolean {
        return this.registrationStatus === 'PENDING';
    }

    /** Bid limit as design-system money ($X,XXX.00, tabular). */
    get spendingCapDisplay(): string {
        return money(this.user?.spendingCap ?? 0);
    }

    /** Stat-card navigation to the related profile panel. */
    goTo(path: string): void {
        this.router.navigate([path]);
    }

    // Pure functions for data transformation
    private adjustUserSpendingCap(user: User): User {
        return {
            ...user,
            spendingCap:
                user.registrationStatus === 'PENDING' ? 0 : user.spendingCap,
        };
    }

    private getSpendingCapMessage(user: User): string | null {
        if (user.registrationStatus === 'PENDING') {
            return 'Until your account is verified by support, you may not place a bid on any item.';
        }
        if (!this.hasCard && user.spendingCap === 500) {
            return 'Add a card to remove this limit.';
        }
        return null;
    }

    private shouldShowBidLimit(user: User): boolean {
        return (
            (user.spendingCap ?? 0) > 0 || user.registrationStatus === 'PENDING'
        );
    }

    get hasSpendingCap(): boolean {
        if (this.user?.spendingCap) {
            return this.user.spendingCap !== 0;
        }
        return false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
